import { getCountryPricing, getDefaultCountry, isCountrySupported } from '../config/configMultiCurrency';

// Country detection utility
export const detectUserCountry = async () => {
  try {
    // First try to get from localStorage (user preference)
    const savedCountry = localStorage.getItem('userCountryCode');
    if (savedCountry && isCountrySupported(savedCountry)) {
      return {
        countryCode: savedCountry,
        currency: getCountryPricing(savedCountry).currency,
        source: 'localStorage'
      };
    }

    // Then try IP-based detection
    const response = await fetch('/api/country-pricing/detect');
    if (response.ok) {
      const data = await response.json();
      if (data.success && isCountrySupported(data.data.countryCode)) {
        // Save detected country to localStorage
        localStorage.setItem('userCountryCode', data.data.countryCode);
        return {
          countryCode: data.data.countryCode,
          currency: data.data.currency,
          source: 'ip'
        };
      }
    }

    // Fallback to browser locale detection
    const browserCountry = detectCountryFromBrowser();
    if (browserCountry && isCountrySupported(browserCountry)) {
      localStorage.setItem('userCountryCode', browserCountry);
      return {
        countryCode: browserCountry,
        currency: getCountryPricing(browserCountry).currency,
        source: 'browser'
      };
    }

    // Final fallback to default
    const defaultCountry = getDefaultCountry();
    localStorage.setItem('userCountryCode', defaultCountry);
    return {
      countryCode: defaultCountry,
      currency: getCountryPricing(defaultCountry).currency,
      source: 'default'
    };

  } catch (error) {
    console.error('Error detecting country:', error);
    const defaultCountry = getDefaultCountry();
    localStorage.setItem('userCountryCode', defaultCountry);
    return {
      countryCode: defaultCountry,
      currency: getCountryPricing(defaultCountry).currency,
      source: 'error-fallback'
    };
  }
};

// Detect country from browser locale
const detectCountryFromBrowser = () => {
  try {
    const locale = navigator.language || navigator.userLanguage;
    if (locale) {
      // Extract country code from locale (e.g., "en-US" -> "US")
      const parts = locale.split('-');
      if (parts.length > 1) {
        return parts[1].toUpperCase();
      }
    }
  } catch (error) {
    console.error('Error detecting country from browser:', error);
  }
  return null;
};

// Set user's preferred country
export const setUserCountry = (countryCode) => {
  if (isCountrySupported(countryCode)) {
    localStorage.setItem('userCountryCode', countryCode);
    return {
      countryCode,
      currency: getCountryPricing(countryCode).currency,
      source: 'manual'
    };
  }
  throw new Error(`Unsupported country code: ${countryCode}`);
};

// Get current user country
export const getCurrentUserCountry = () => {
  const savedCountry = localStorage.getItem('userCountryCode');
  if (savedCountry && isCountrySupported(savedCountry)) {
    return {
      countryCode: savedCountry,
      currency: getCountryPricing(savedCountry).currency
    };
  }
  
  const defaultCountry = getDefaultCountry();
  return {
    countryCode: defaultCountry,
    currency: getCountryPricing(defaultCountry).currency
  };
};

// Get country pricing for current user
export const getCurrentCountryPricing = () => {
  const { countryCode } = getCurrentUserCountry();
  return getCountryPricing(countryCode);
}; 